/**
 * Thin, crash-proof wrapper around Firebase Crashlytics + Analytics.
 *
 * @react-native-firebase ships native modules that ONLY exist in an Android
 * build - they are absent on web, in Expo Go, and on iOS (we don't configure
 * iOS). So the rest of the app must never `import '@react-native-firebase/*'`
 * directly; it goes through this module instead.
 *
 * Strategy (mirrors the AdMob / live-notification guards):
 *  - every export is a NO-OP unless `Platform.OS === 'android'`,
 *  - the native modules are `require`d lazily inside a try/catch the first time
 *    they're needed, so a missing/erroring module never throws into the app,
 *  - all calls are best-effort: telemetry must never crash the product.
 */
import { Platform } from 'react-native';

type CrashlyticsModule = {
  setCrashlyticsCollectionEnabled: (enabled: boolean) => Promise<void>;
  recordError: (error: Error) => void;
  log: (message: string) => void;
};

type AnalyticsModule = {
  logEvent: (name: string, params?: Record<string, unknown>) => Promise<void>;
  setUserId: (id: string | null) => Promise<void>;
};

const isAndroid = Platform.OS === 'android';

let crashlyticsLoaded = false;
let analyticsLoaded = false;
let crashlytics: (() => CrashlyticsModule) | null = null;
let analytics: (() => AnalyticsModule) | null = null;

function getCrashlytics(): CrashlyticsModule | null {
  if (!isAndroid) return null;
  if (!crashlyticsLoaded) {
    crashlyticsLoaded = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      crashlytics = require('@react-native-firebase/crashlytics').default;
    } catch {
      crashlytics = null;
    }
  }
  try {
    return crashlytics ? crashlytics() : null;
  } catch {
    return null;
  }
}

function getAnalytics(): AnalyticsModule | null {
  if (!isAndroid) return null;
  if (!analyticsLoaded) {
    analyticsLoaded = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      analytics = require('@react-native-firebase/analytics').default;
    } catch {
      analytics = null;
    }
  }
  try {
    return analytics ? analytics() : null;
  } catch {
    return null;
  }
}

/** Turn an unknown thrown value into a real Error for Crashlytics. */
function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  try {
    return new Error(typeof error === 'string' ? error : JSON.stringify(error));
  } catch {
    return new Error('Unknown error');
  }
}

/**
 * Enable Crashlytics crash collection. Call once at startup. No-op on
 * web / Expo Go / iOS or if the native module is unavailable.
 */
export async function initCrashReporting(): Promise<void> {
  const c = getCrashlytics();
  if (!c) return;
  try {
    await c.setCrashlyticsCollectionEnabled(true);
  } catch {
    // Best-effort: never block startup on telemetry.
  }
}

/** Report a non-fatal error to Crashlytics. Best-effort; never throws. */
export function recordError(error: unknown): void {
  const c = getCrashlytics();
  if (!c) return;
  try {
    c.recordError(toError(error));
  } catch {
    // Swallow: telemetry must never crash the app.
  }
}

/** Log an Analytics event. Best-effort; never throws. */
export function logEvent(name: string, params?: Record<string, unknown>): void {
  const a = getAnalytics();
  if (!a) return;
  try {
    void a.logEvent(name, params).catch(() => {
      // Ignore network / module errors.
    });
  } catch {
    // Swallow.
  }
}

/** Associate analytics + crash reports with a user id (or clear it). */
export function setAnalyticsUser(id: string | null): void {
  const a = getAnalytics();
  if (!a) return;
  try {
    void a.setUserId(id).catch(() => {
      // Ignore.
    });
  } catch {
    // Swallow.
  }
}
